/**
 * Onboarding Assistant Floating Button
 *
 * A floating action button that opens the AI assistant chat panel.
 * Features:
 * - Smart notification dot (urgent/warning/info)
 * - Contextual nudge balloon with dismissible messages
 * - Custom AI bubble icon with breathing animation
 */

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface OnboardingAssistantButtonProps {
  isOpen: boolean;
  onClick: () => void;
  isNewUser?: boolean;
  showBadge?: boolean;
  className?: string;
  /** Notification dot alert */
  hasAlert?: boolean;
  alertType?: 'urgent' | 'warning' | 'info' | null;
  /** Nudge balloon text */
  nudgeText?: string | null;
}

export function OnboardingAssistantButton({
  isOpen,
  onClick,
  isNewUser = false,
  showBadge = false,
  className,
  hasAlert = false,
  alertType = null,
  nudgeText = null,
}: OnboardingAssistantButtonProps) {
  const { t } = useTranslation();
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [nudgeVisible, setNudgeVisible] = useState(false);

  // Show nudge balloon with a delay after mount, auto-hide after 8s
  useEffect(() => {
    if (!nudgeText || isOpen || nudgeDismissed) {
      setNudgeVisible(false);
      return;
    }
    const showTimer = setTimeout(() => setNudgeVisible(true), 2000);
    const hideTimer = setTimeout(() => setNudgeVisible(false), 10000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [nudgeText, isOpen, nudgeDismissed]);

  // Reset dismiss when nudge text changes
  useEffect(() => {
    setNudgeDismissed(false);
  }, [nudgeText]);

  // Dot color based on alert type
  const dotColors = {
    urgent: 'bg-red-500 shadow-red-500/50',
    warning: 'bg-amber-500 shadow-amber-500/50',
    info: 'bg-blue-500 shadow-blue-500/50',
  };

  return (
    <div className={cn('fixed z-50 bottom-6 right-4 md:right-6', className)}>
      {/* Nudge Balloon */}
      {nudgeVisible && nudgeText && !isOpen && (
        <div
          className={cn(
            'absolute bottom-[calc(100%+8px)] right-0',
            'w-max max-w-72 px-3 py-2 whitespace-normal',
            'bg-white dark:bg-slate-800 rounded-xl',
            'shadow-lg shadow-black/10 dark:shadow-black/30',
            'border border-slate-200 dark:border-slate-700',
            'text-sm text-slate-700 dark:text-slate-200 font-medium',
            'animate-in fade-in slide-in-from-bottom-2 duration-300',
          )}
        >
          <span>{nudgeText}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setNudgeDismissed(true);
              setNudgeVisible(false);
            }}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
          {/* Arrow pointing down */}
          <div className="absolute -bottom-1.5 right-4 w-3 h-3 rotate-45 bg-white dark:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700" />
        </div>
      )}

      {/* Main Button */}
      <button
        onClick={onClick}
        className={cn(
          'relative flex items-center justify-center',
          'w-12 h-12 rounded-full shadow-lg',
          'transition-all duration-300 ease-in-out',
          isOpen
            ? 'bg-gradient-to-br from-cyan-400 via-cyan-500 to-blue-500 hover:from-cyan-500 hover:via-cyan-600 hover:to-blue-600 shadow-xl'
            : 'bg-transparent border border-cyan-400/60 dark:border-cyan-400/40 hover:scale-110 hover:border-cyan-400 dark:hover:border-cyan-400',
          !isOpen && 'hover:shadow-xl hover:shadow-blue-500/30',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
          isNewUser && !isOpen && 'animate-[breathing_2s_ease-in-out_infinite]',
        )}
        style={{
          ...(isNewUser && !isOpen ? {
            animation: 'breathing 2s ease-in-out infinite',
          } : {})
        }}
        aria-label={isOpen
          ? t('onboarding.assistantButton.closeAssistant', 'Close assistant')
          : t('onboarding.assistantButton.openAssistant', 'Open AI assistant')
        }
      >
        {/* Icon */}
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <img
            src="/ai-bubble.png"
            alt="AI Assistant"
            className="w-full h-full rounded-full object-cover"
          />
        )}

        {/* Smart Notification Dot */}
        {!isOpen && hasAlert && alertType && !showBadge && !isNewUser && (
          <span className={cn(
            'absolute -top-0.5 -right-0.5',
            'w-3.5 h-3.5 rounded-full',
            'border-2 border-white dark:border-slate-900',
            'shadow-md',
            dotColors[alertType],
            alertType === 'urgent' && 'animate-pulse',
          )} />
        )}

        {/* Badge for new users (takes priority over dot) */}
        {!isOpen && (showBadge || isNewUser) && (
          <span className={cn(
            'absolute -top-1 -right-1',
            'flex items-center justify-center',
            'min-w-[24px] h-6 px-1.5',
            'text-xs font-bold text-white',
            'bg-gradient-to-r from-rose-500 to-pink-500 rounded-full',
            'shadow-md shadow-rose-500/30',
            'animate-pulse'
          )}>
            {t('onboarding.assistantButton.help', 'Help')}
          </span>
        )}

        {/* Soft glow effect for new users */}
        {!isOpen && isNewUser && (
          <span className="absolute inset-0 rounded-full bg-cyan-400 opacity-20 animate-ping" />
        )}
      </button>

      {/* Inline keyframes */}
      <style>{`
        @keyframes breathing {
          0%, 100% { transform: scale(1); box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.3); }
          50% { transform: scale(1.05); box-shadow: 0 20px 25px -5px rgba(99, 102, 241, 0.4); }
        }
      `}</style>
    </div>
  );
}

export default OnboardingAssistantButton;
